<template>
	<div class="container mt-4 px-2">
		<div class="dashboard-section">
			<div class="section-header d-flex justify-content-between align-items-center mb-4">
				<h2 class="section-title"><i class="fas fa-calendar-plus"></i> Nouvelle demande de congés</h2>
			</div>

			<n-card class="leave-form-card">
				<n-form
					ref="formRef"
					:model="model"
					:rules="rules"
					:label-placement="isMobile ? 'top' : 'left'"
					:label-width="isMobile ? 'auto' : '200'"
					require-mark-placement="right-hanging"
					:size="isMobile ? 'small' : 'medium'"
				>

					<n-form-item label="Motif de la demande" path="type">
						<n-radio-group v-model:value="model.type" name="type">
							<n-space :vertical="isMobile" :wrap="!isMobile" :size="isMobile ? 'small' : 'medium'">
								<n-radio-button value="conges">Demande de congés</n-radio-button>
								<n-radio-button value="extraordinaires">Congés extraordinaires</n-radio-button>
								<n-radio-button value="sickleave">Arrêt de travail</n-radio-button>
							</n-space>
						</n-radio-group>
					</n-form-item>

					<n-form-item v-if="model.type === 'extraordinaires'" label="Type de congé extraordinaire"
								 path="extraordinaryType">
						<n-select
							v-model:value="model.extraordinaryType"
							:options="model.type === 'extraordinaires' ? extraordinaryOptions : sickleaveOptions"
							placeholder="Sélectionnez le type"
						/>
					</n-form-item>

					<n-alert v-if="model.type === 'extraordinaires'" type="info" class="mb-4">
						<template #icon>
							<i class="fas fa-info-circle"></i>
						</template>
						<p class="mb-2">
							<strong>Détail légal (art. L.233-16 du Code du travail luxembourgeois) :</strong>
						</p>
						<ul class="mb-2 ps-3">
							<li>Mariage du salarié : 3 jours</li>
							<li>Mariage d'un enfant : 1 jour (par parent)</li>
							<li>Naissance : 10 jours (père / 2e parent)</li>
							<li>Adoption : 10 jours (enfant de moins de 16 ans accueilli au foyer)</li>
							<li>Décès du conjoint/partenaire : 3 jours</li>
							<li>Décès d'un parent au 1er degré (parents du salarié ou de son conjoint/partenaire) : 3 jours</li>
							<li>Décès d'un parent au 2e degré (grands-parents, petits-enfants, frères/sœurs, beaux-frères/belles-sœurs) : 1 jour</li>
							<li>Déménagement : 2 jours (1 fois par période de 3 ans (Jérémy 💖...) , sauf déménagement imposé pour raison professionnelle)</li>
						</ul>
						<p class="mb-0">
							Ces congés sont payés à 100%, ne sont pas déduits des congés ordinaires et sont accessibles dès le 1er jour de travail.
						</p>
					</n-alert>

					<n-alert v-if="model.type === 'sickleave'" type="info" class="mb-4">
						<template #icon>
							<i class="fas fa-info-circle"></i>
						</template>
						<p class="mb-2">
							<strong>Rappel important :</strong> J'envoie mon certificat d'incapacité de travail à la CNS
							au plus tard avant l'expiration du 3e jour ouvré de
							l'arrêt de travail.
						</p>
						<p class="mb-2">
							Je l'envoie en utilisant le
							<n-button text tag="a" type="primary"
									  href="https://cns.public.lu/fr/assure/services-ligne/demarches-incapacite/formulaire-declaration-incapacite-travail.html"
									  target="_blank">
								formulaire de déclaration en ligne <i class="fas fa-external-link-alt ms-1"></i>
							</n-button>
							. J'y joins obligatoirement mon arrêt de maladie en pièce jointe au format PDF.
						</p>
						<p class="mb-0">
							Si je ne peux pas l'envoyer via ce formulaire, j'envoie mon arrêt de maladie par courrier
							postal. Le cachet
							de la poste fait foi pour prouver que je l'ai envoyé dans les délais.
						</p>
					</n-alert>

					<!-- si le congé est extraordinaire il faut un justificatif PDF ou IMG -->
					<n-form-item v-if="model.type === 'extraordinaires' || model.type === 'sickleave'"
								 label="Justificatif"
								 path="justificatif">
						<div class="justificatif-field">
							<n-upload
								v-model:file-list="model.justificatif"
								accept=".pdf,.jpg,.jpeg,.png"
							>
								<n-button size="small" type="primary">
									Sélectionner un fichier
								</n-button>
							</n-upload>
							<n-button size="small" @click="showCapture = true" class="mt-2">
								<template #icon>
									<i class="fas fa-camera"></i>
								</template>
								Prendre en photo
							</n-button>
						</div>
					</n-form-item>

					<PhotoCapture
						v-if="showCapture"
						@done="handleCaptureDone"
						@cancel="showCapture = false"
					/>


					<n-form-item label="Date de début de congés" path="start">
						<n-date-picker format="dd/MM/yyyy" v-model:value="model.start" type="date"
									   input-readonly
									   :is-date-disabled="dateDisabled"/>
					</n-form-item>

					<n-form-item label="Date de fin de congés" path="end">
						<n-date-picker format="dd/MM/yyyy" v-model:value="model.end"
									   :is-date-disabled="dateAfterStartDate"
									   input-readonly
									   type="date"/>
					</n-form-item>

					<!-- commentaire -->
					<n-form-item label="Commentaire" path="comment">
						<n-input v-model:value="model.comment" type="textarea"/>
					</n-form-item>
					<div class="d-flex justify-content-end mt-4">
						<n-button @click="router.push('/leaves')" :loading="loading" class="me-2">
							<template #icon>
								<i class="fas fa-arrow-left me-2"></i>
							</template>
							Retour
						</n-button>
						<n-button @click="handleValidateClick" type="primary" :loading="loading">
							<template #icon>
								<i class="fas fa-paper-plane me-1"></i>
							</template>
							Soumettre la demande
						</n-button>
					</div>
				</n-form>
			</n-card>
		</div>
	</div>

</template>

<script setup>
import {computed, ref, onMounted, onUnmounted} from 'vue'
import {useMessage} from 'naive-ui'
import {useRouter} from 'vue-router'
import {useStore} from 'vuex'
import moment from "moment/moment.js";
import PhotoCapture from './PhotoCapture.vue'

const formRef = ref(null)
const message = useMessage()
const loading = ref(false)
const isMobile = ref(false)
const showCapture = ref(false)

// Détection du mode mobile
const checkScreenSize = () => {
	isMobile.value = window.innerWidth < 768
}

onMounted(() => {
	checkScreenSize()
	window.addEventListener('resize', checkScreenSize)
})

onUnmounted(() => {
	window.removeEventListener('resize', checkScreenSize)
})

const model = ref({
	name: '',
	requestDate: null,
	type: 'conges',
	extraordinaryType: null,
	start: null,
	end: null,
	justificatif: [],
	comment: ''
})

const minStartDate = computed(() => {
	let yesterday = new Date();
	yesterday.setDate(yesterday.getDate() - 1);
	return yesterday
});

const dateDisabled = (date) => {
	// Pour les congés extraordinaires et les arrêts de travail, la date de début
	// peut être antérieure à aujourd'hui (déclaration a posteriori).
	if (model.value.type === 'extraordinaires' || model.value.type === 'sickleave') {
		return false
	}
	return date <= minStartDate.value
}

const dateAfterStartDate = (date) => {
	return date < model.value.start
}

const extraordinaryOptions = [
  { label: 'Adoption (10 jours)', value: 'adoption' },
	{label: 'Décès conjoint/partenaire (3 jours)', value: 'deathPartner'},
	{label: 'Décès 1er degré (3 jours)', value: 'death1stDegree'},
	{label: 'Décès 2ème degré (1 jour)', value: 'death2ndDegree'},
	{label: 'Déménagement (2 jours)', value: 'moving'},
	{label: 'Mariage (3 jours)', value: 'marriage'},
  { label: 'Mariage d\'un enfant (1 jours)', value: 'childMarriage' },
  { label: 'Naissance (10 jours)', value: 'birth' },
];

const sickleaveOptions = [
	// arret de travail
	{label: 'Arrêt de travail maladie (<3 jours)', value: 'sickleave'},
	{label: 'Arrêt de travail maladie (>=3 jours)', value: 'sickleaveLong'},
]

const rules = {
	type: {
		required: true,
		message: 'Veuillez sélectionner le motif de la demande',
		trigger: 'change'
	},
	start: {
		type: 'date',
		required: true,
		message: 'Veuillez sélectionner la date de début',
		trigger: ['blur', 'change']
	},
	end: {
		type: 'date',
		required: true,
		message: 'Veuillez sélectionner la date de fin',
		trigger: ['blur', 'change']
	},
	justificatif: {
		type: 'array',
		required: true,
		message: 'Veuillez fournir un justificatif',
		trigger: ['change'],
		validator: (rule, value) => {
			if (model.value.type === 'extraordinaires' || model.value.type === 'sickleave') {
				return value && value.length > 0;
			}
			return true;
		}
	},
}

const handleCaptureDone = (file) => {
	model.value.justificatif.push({
		id: `photo-${file.name}`,
		name: file.name,
		status: 'finished',
		file,
	})
	showCapture.value = false
}

const store = useStore()
const router = useRouter()
const handleValidateClick = async (e) => {
	e.preventDefault();
	loading.value = true;

	try {
		await formRef.value.validate((errors) => {
			if (errors) {
				// Des erreurs de validation existent
				console.log('Validation errors:', errors);
				message.error('Veuillez remplir correctement tous les champs requis');
				return false;
			}

			// Pas d'erreurs, on peut soumettre le formulaire
			return true;
		});

		// Si on arrive ici, c'est que la validation a réussi
		await store.dispatch('newleaves', {
			type: model.value.type === 'conges' ? 'conges' : model.value.extraordinaryType,
			files: model.value.justificatif,
			comment: model.value.comment,
			extraordinaryType: model.value.extraordinaryType,
			start: moment(model.value.start).format('YYYY-MM-DD'),
			end: moment(model.value.end).format('YYYY-MM-DD'),
		});

		message.success('Demande de congés soumise avec succès');
		router.push('/leaves');

	} finally {
		loading.value = false;
	}
}
</script>

<style scoped>
.dashboard-section {
	background-color: white;
	border-radius: 8px;
	padding: 1rem;
}

.section-header {
	margin-bottom: 2rem;
}

.section-title {
	font-size: 1.5rem;
	margin: 0;
	display: flex;
	align-items: center;
	gap: 0.5rem;
}

.section-title i {
	color: #2080f0;
}

:deep(.n-card) {
	box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.03), 0 1px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px 0 rgba(0, 0, 0, 0.02);
}

:deep(.n-form-item) {
	margin-bottom: 1.5rem;
}

:deep(.n-upload) {
	width: 100%;
}

.justificatif-field {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 0.25rem;
}

:deep(.n-input-wrapper) {
	width: 100%;
}

:deep(.n-date-picker) {
	width: 100%;
	max-width: 300px;
}

:deep(.n-alert) {
	margin: 1rem 0;
}

:deep(.n-alert p:last-child) {
	margin-bottom: 0;
}

/* Responsive styles */
@media (max-width: 768px) {
	.section-title {
		font-size: 1.25rem;
	}

	.section-header {
		margin-bottom: 1rem;
	}

	.dashboard-section {
		padding: 0.75rem;
	}

	:deep(.n-form-item) {
		margin-bottom: 1rem;
	}

	:deep(.n-form-item-label) {
		padding-bottom: 4px;
		font-weight: 500;
	}

	:deep(.n-date-picker) {
		max-width: 100%;
	}

	:deep(.n-card-content) {
		padding: 12px;
	}

	:deep(.n-radio-button) {
		padding: 0 8px;
		font-size: 0.9rem;
		height: 32px;
		line-height: 32px;
	}

	:deep(.n-alert) {
		padding: 8px 12px;
		font-size: 0.9rem;
	}

	:deep(.n-alert p) {
		margin-bottom: 0.5rem;
	}

	:deep(.n-button) {
		padding: 0 12px;
	}
}
</style>